import * as _ from "lodash";
import { AdminListSort } from "./admin-list-sort";
import * as activity from "./admin-activity.helper";
import * as angular from "angular";
import { AdminDataProvider } from "../../../data-provider/admin.dp";
import { UserDataProvider } from "../../../data-provider/user.dp";
import { Organisation } from "../../../models/organisation.model";
import { User } from "../../../models/user.model";
import { OrganisationService } from "../../../services/organisation.service";
import { CredentialsManager } from "../../../shared/credentials-manager";
import { CampaignService } from "../../../services/campaign.service";
import { DialogUtils } from "../../../helper/dialog.helper";
import { ToastUtils } from "../../../helper/toast.helper";
import { XeroService } from "../../../services/xero.service";

let AdminOrganisationListComponent = {
    selector: "vcAdminOrganisationsList",
    templateUrl:
        "/angular/components/admin/lists/organisations-list.component.html",
    bindings: {
        filter: "=",
    },
    controller: class AdminOrganisationsListController {
        // * bindings for typescript compiler *
        public filter: any;
        // ************************************

        /** Placeholder rows while the first page loads, so the table keeps its height. */

        public skeleton = new Array(8);

        /** Header sorting (AV-17963), same keys as the Accounts list. */
        public sorting = new AdminListSort(
            {
                oid: "id",
                houseoccupantslimit: "contacts",
                houses: "houses",
                interactions: "interactions",
                // Activity lens columns (AV-17958)
                a_interactions: "interactions",
                a_logins: "logins",
                a_activeusers: "activeusers",
                a_contacts: "contacts",
                a_lastactivity: "last_active",
                a_trend: "trend",
            },
            () => this.filter
        );
        private allColumns: any;

        statNumber(organisation: Organisation, key: string): string {
            return AdminListSort.statNumber(organisation, key);
        }

        // ---------- Activity lens (AV-17958) ----------

        activity(): boolean {
            return !!this.filter && this.filter.lens == "activity";
        }

        columnLabel(column: { id: string; name: string }): string {
            if (column.id == "a_interactions" || column.id == "a_logins") {
                return (
                    column.name + " · " + activity.rangeLabel(this.filter.range)
                );
            }
            return column.name;
        }

        emptyText(): string {
            return this.activity()
                ? activity.emptyCopy(
                      this.filter.trend,
                      "organisations",
                      this.filter.range
                  )
                : "Nothing matches these filters.";
        }

        deltaText = activity.deltaText;
        deltaClass = activity.deltaClass;
        relativeDays = activity.relativeDays;
        lastActivityClass = activity.lastActivityClass;
        shortDate = activity.shortDate;
        trendKey = activity.trendKey;
        trendLabel = activity.trendLabel;
        trendIcon = activity.trendIcon;
        sparkPoints = activity.sparkPoints;
        sparkLast = activity.sparkLast;

        /**
         * AV-18115: the Accounts count is a way in, not just a number - it
         * opens the list so an admin can join one without going to the
         * accounts tab and searching for it.
         */
        openAccounts(organisation: Organisation, $event: any): void {
            if (
                !organisation["stats_accounts"] &&
                !organisation["stats_accounts_cancelled"]
            ) {
                return;
            }
            this.dialog.show({
                component: "vc-admin-org-accounts-dialog",
                targetEvent: $event,
                size: "large",
                locals: {
                    oid: organisation.oid,
                    organisationName: organisation["oname"],
                },
            });
        }

        /**
         * The count is the live accounts; cancelled ones are named so the
         * number and the dialog (which lists them greyed at the end) agree.
         */
        accountsTooltip(organisation: Organisation): string {
            const live = Number(organisation["stats_accounts"]) || 0;
            const cancelled =
                Number(organisation["stats_accounts_cancelled"]) || 0;
            const plural = (n: number): string => (n == 1 ? "" : "s");
            if (!live && !cancelled) {
                return "No accounts in this organisation";
            }
            if (!live) {
                return (
                    "No live accounts; show the " +
                    cancelled +
                    " cancelled account" +
                    plural(cancelled)
                );
            }
            return (
                "Show the " +
                live +
                " account" +
                plural(live) +
                " in this organisation" +
                (cancelled
                    ? ", plus " + cancelled + " cancelled"
                    : "")
            );
        }

        openActivityDialog(organisation: Organisation, $event: any): void {
            this.dialog.show({
                component: "vc-admin-activity-dialog",
                targetEvent: $event,
                size: "large",
                locals: {
                    kind: "organisation",
                    recordId: organisation.oid,
                    range: this.filter.range || 7,
                },
            });
        }

        private loading: boolean;
        private searchCanceller: any;
        private organisations: Organisation[];
        private readonly user: User;

        public columns: object[];
        private $scope: any;
        private toast: ToastUtils;
        private dialog: DialogUtils;
        private service: OrganisationService;
        private userDataProvider: UserDataProvider;
        public dataProvider: AdminDataProvider;
        public searchDelay: any;
        private readonly $interval: any;

        public __env: any;
        public $q: any;

        /** True while a join-and-open is in flight; disables every row link. */
        public entering = false;
        /** Row selection for the bulk "remove me" action (oid -> true). */
        public selected: { [oid: string]: boolean } = {};
        public removing = false;
        private credentialsManager: CredentialsManager;
        private campaignService: CampaignService;
        private readonly $cookies: any;

        constructor(
            $scope: any,
            $interval: any,
            $cookies: any,
            CredentialsManager: CredentialsManager,
            CampaignService: CampaignService,
            OrganisationService: OrganisationService,
            AdminDataProvider: AdminDataProvider,
            UserDataProvider: UserDataProvider,
            ToastUtils: ToastUtils,
            DialogUtils: DialogUtils,
            __env: any,
            $q: any
        ) {
            this.$scope = $scope;
            this.$interval = $interval;
            this.$cookies = $cookies;
            this.credentialsManager = CredentialsManager;
            this.campaignService = CampaignService;
            this.$q = $q;
            this.dataProvider = AdminDataProvider;
            this.service = OrganisationService;
            this.userDataProvider = UserDataProvider;
            this.toast = ToastUtils;
            this.dialog = DialogUtils;
            this.__env = __env;
            this.user = this.userDataProvider.getActiveUser();
            this.allColumns = this.dataProvider.getColumns();
            this.columns = this.allColumns.organisations;
        }

        $onInit() {
            // AV-17958: the lens picks the column set.
            this.$scope.$watch(
                () => this.filter && this.filter.lens,
                (lens: string | undefined) => {
                    this.columns =
                        this.allColumns[
                            AdminDataProvider.columnsKey("organisations", lens)
                        ] || this.allColumns.organisations;
                }
            );
            this.$scope.$watch(
                () => this.filter,
                (newValue: object, oldValue: object) => {
                    if (oldValue && newValue !== oldValue) {
                        if (this.searchDelay) {
                            this.searchDelay = this.$interval.cancel(
                                this.searchDelay
                            );
                        }
                        this.searchDelay = this.$interval(() => {
                            this.$interval.cancel(this.searchDelay);
                            if (this.searchCanceller) {
                                this.searchCanceller.resolve("User cancelled");
                            }
                            this.searchCanceller = this.$q.defer();
                            this.loadOrganisations(true);
                        }, 500);
                    }
                },
                true
            );

            this.loadOrganisations(true);
        }

        /** Increments per counts request; a late response is ignored. */
        private chipCountsRequest = 0;
        /** The filters the current chip counts answer for. */
        private chipCountsKey: string | null = null;

        loadOrganisations(reload: boolean): void {
            if (
                reload ||
                !this.organisations ||
                this.organisations.length == 0
            ) {
                this.loading = true;
                this.loadChipCounts();
                this.service
                    .admin(
                        this.filter,
                        this.searchCanceller
                            ? this.searchCanceller.promise
                            : undefined
                    )
                    .then((response: object) => {
                        this.loading = false;
                        const json = response["data"];
                        if (json.success) {
                            this.organisations = json.data;
                            this.selected = {};
                        } else {
                            this.organisations = [];
                        }
                    });
            }
        }

        /**
         * What the chip counts depend on. Sorting is left out on purpose: the
         * order of the rows never changes how many there are, so a header
         * click must not cost a second counting query.
         */
        private chipCountsKeyFor(): string {
            const filter: any = angular.copy(this.filter || {});
            delete filter.sort;
            delete filter.sortDir;
            // Chip fields are covered by the counts themselves (the status
            // chip is ignored, the Activity lens returns a status x trend
            // grid), so a chip click never needs a new count.
            delete filter.ostatus;
            delete filter.trend;
            return angular.toJson(filter);
        }

        /**
         * The numbers on the status chips, fetched in parallel with the rows
         * so the list never waits on the counting query. Only refetched when
         * the filters change; a response that arrives after they have moved
         * on again is dropped, so a slow count can never label the wrong list.
         */
        private loadChipCounts(): void {
            const key = this.chipCountsKeyFor();
            if (key === this.chipCountsKey) {
                return;
            }
            this.chipCountsKey = key;
            delete this.dataProvider.chipCounts.organisations;
            delete this.dataProvider.chipCounts["organisations:grid"];
            const token = ++this.chipCountsRequest;
            this.service
                .adminChipCounts(this.filter)
                .then((response: object) => {
                    if (token !== this.chipCountsRequest) {
                        return;
                    }
                    const json = response["data"];
                    if (json && json.data && json.data.grid) {
                        // Activity lens: status x trend; the chips sum it.
                        this.dataProvider.chipCounts["organisations:grid"] =
                            json.data.grid;
                    } else if (json && json.data) {
                        this.dataProvider.chipCounts.organisations = json.data;
                    }
                });
        }

        /**
         * AV-18115: create a sub account straight into this organisation.
         * The create dialog is the same one the header uses; handing it the
         * organisation skips its first step and fixes the target, so the
         * account cannot land under the wrong one.
         */
        addAccount(organisation: Organisation, $event: any): void {
            this.dialog
                .show({
                    component: "vc-admin-create-campaign",
                    targetEvent: $event,
                    size: "large",
                    locals: {
                        presetOrganisation: {
                            oid: organisation.oid,
                            oname: organisation["oname"],
                        },
                    },
                })
                .then(
                    () => {
                        // A new account changes the row's Accounts count.
                        this.chipCountsKey = null;
                        this.loadOrganisations(true);
                    },
                    () => {
                        // Cancelled; nothing changed.
                    }
                );
        }

        // ---------- pinned rows ----------
        // The organisations endpoint returns every row, so pins are only a
        // client-side reorder.

        isPinned(organisation: Organisation): boolean {
            return this.dataProvider.isPinned(
                "organisations",
                organisation.oid
            );
        }

        togglePin(organisation: Organisation): void {
            const state = this.dataProvider.togglePin(
                "organisations",
                organisation.oid
            );
            if (state === "limit") {
                this.toast.error(
                    "You can pin up to " +
                        AdminDataProvider.PIN_LIMIT +
                        " organisations"
                );
            }
        }

        /** Pinned first (in pin order), then everything else as returned. */
        displayRows(): Organisation[] {
            const rows = this.organisations || [];
            const pins = this.dataProvider.getPins("organisations");
            if (!pins.length) return rows;
            const pinned = rows
                .filter((o: Organisation) => pins.indexOf(Number(o.oid)) !== -1)
                .sort(
                    (a: Organisation, b: Organisation) =>
                        pins.indexOf(Number(a.oid)) -
                        pins.indexOf(Number(b.oid))
                );
            return pinned.concat(
                rows.filter(
                    (o: Organisation) => pins.indexOf(Number(o.oid)) === -1
                )
            );
        }

        isLastPinned(organisation: Organisation): boolean {
            const pinned = this.displayRows().filter((o: Organisation) =>
                this.isPinned(o)
            );
            return (
                pinned.length > 0 &&
                pinned[pinned.length - 1].oid == organisation.oid
            );
        }

        // ---------- bulk "remove me" (Accounts I'm On only) ----------

        toggleSelect(organisation: Organisation): void {
            const key = String(organisation.oid);
            if (this.selected[key]) delete this.selected[key];
            else this.selected[key] = true;
        }

        selectedCount(): number {
            return Object.keys(this.selected).length;
        }

        private memberRows(): Organisation[] {
            return this.displayRows().filter(
                (o: Organisation) => o.member == 1 || o.member == "1"
            );
        }

        allLoadedSelected(): boolean {
            const rows = this.memberRows();
            return (
                rows.length > 0 &&
                rows.every((o: Organisation) => !!this.selected[String(o.oid)])
            );
        }

        toggleSelectAll(): void {
            if (this.allLoadedSelected()) {
                this.selected = {};
                return;
            }
            const next: { [id: string]: boolean } = {};
            this.memberRows().forEach((o: Organisation) => {
                next[String(o.oid)] = true;
            });
            this.selected = next;
        }

        clearSelection(): void {
            this.selected = {};
        }

        /**
         * Removes the admin from every selected organisation one call at a
         * time, then reloads the list so rows reflect the server.
         */
        removeSelected($event: any): void {
            const ids = Object.keys(this.selected).map((id) => Number(id));
            if (!ids.length || this.removing) return;
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        title:
                            "Remove yourself from " +
                            ids.length +
                            " organisation" +
                            (ids.length == 1 ? "" : "s") +
                            "?",
                        bodyText:
                            "<p>You will lose access to the selected organisations and their accounts until you add yourself again.</p>",
                    },
                })
                .then(() => {
                    this.removing = true;
                    this.service.adminBulkRemoveUser(ids, this.user.uid).then(
                        (response: object) => {
                            const json = response["data"];
                            this.removing = false;
                            this.selected = {};
                            // Laravel's response() wraps the repository result
                            // as { success, data: { removed, failed } }.
                            const result = (json && json.data) || {};
                            const removed = Number(result.removed) || 0;
                            // An explicit count wins, 0 included; with no
                            // count, a failed request failed them all.
                            const failed =
                                result.failed != null
                                    ? Number(result.failed) || 0
                                    : json && json.success === false
                                    ? ids.length
                                    : 0;
                            const summary =
                                "Removed from " +
                                removed +
                                " organisation" +
                                (removed == 1 ? "" : "s") +
                                (failed ? ", " + failed + " failed" : "");
                            if (failed) this.toast.error(summary);
                            else this.toast.success(summary);
                            this.chipCountsKey = null;
                            this.loadOrganisations(true);
                        },
                        () => {
                            this.removing = false;
                            this.toast.error(
                                "Could not remove you from the selected organisations"
                            );
                        }
                    );
                });
        }

        isPastDeletionDate(organisationDeletionDate: string): boolean {
            if (!organisationDeletionDate) {
                return false;
            }
            let deletionDate: Date = new Date(organisationDeletionDate);
            let now: Date = new Date();

            return deletionDate < now;
        }

        /**
         * Joins the current admin to the organisation (which also seats them
         * on all its campaigns), re-authenticates against it and opens the
         * leader view.
         */
        joinAndOpenLeaderView(organisation: Organisation): void {
            if (this.entering) return;
            this.entering = true;
            const open = (): void => this.switchAndOpenLeaderView(organisation);
            if (organisation.member == 1 || organisation.member == "1") {
                open();
                return;
            }
            const data = angular.extend(_.cloneDeep(organisation), this.user);
            this.service.addAdmin(data).then(
                (response: object): void => {
                    const json = response["data"];
                    if (json.success) {
                        organisation.member = 1;
                        open();
                    } else {
                        this.entering = false;
                        this.toast.error(
                            json.message || "Could not join the organisation"
                        );
                    }
                },
                (): void => {
                    this.entering = false;
                    this.toast.error("Could not join the organisation");
                }
            );
        }

        /**
         * The leader view needs the live session inside one of the
         * organisation's campaigns, so switch into it (the admin is seated on
         * all of them once a member), re-authenticate and open leader.php.
         */
        private switchAndOpenLeaderView(organisation: Organisation): void {
            const destination = "/leader.php/#/divisions";
            if (!organisation.first_campaignid) {
                this.entering = false;
                this.toast.error("This organisation has no accounts to open");
                return;
            }
            this.campaignService
                .live({ campaignid: organisation.first_campaignid })
                .then(
                    (response: any): void => {
                        const json: any = response.data;
                        if (json.success) {
                            this.$cookies.remove("signedIn");
                            this.credentialsManager
                                .reauth(
                                    json.campaign.campaignid,
                                    organisation.oid
                                )
                                .then((): void => {
                                    window.location.href = destination;
                                });
                        } else if (json.region && json.handoff) {
                            // Hosted in the other region: the API minted a
                            // single-use token and has already dropped our
                            // session here, so carry it over rather than stop.
                            this.credentialsManager.handoff(
                                json.region,
                                json.handoff,
                                destination
                            );
                        } else if (json.region) {
                            this.entering = false;
                            this.toast.error(
                                "This organisation is hosted in the '" +
                                    json.region +
                                    "' region — open it from that region's admin panel"
                            );
                        } else {
                            this.entering = false;
                            this.toast.error(
                                json.message ||
                                    "Could not switch into the organisation"
                            );
                        }
                    },
                    (): void => {
                        this.entering = false;
                        this.toast.error(
                            "Could not switch into the organisation"
                        );
                    }
                );
        }

        addToOrganisation(organisation: Organisation): void {
            const data = angular.extend(_.cloneDeep(organisation), this.user);
            this.service.addAdmin(data).then((response: object) => {
                const json = response["data"];
                if (json.success) {
                    organisation.member = 1;
                    this.toast.success("Added to Organization");
                }
            });
        }

        removeFromOrganisation(organisation: Organisation): void {
            const data = angular.extend(_.cloneDeep(organisation), this.user);
            this.service.adminRemoveUser(data).then((response: object) => {
                const json = response["data"];
                if (json.success) {
                    organisation.member = 0;
                    this.toast.success("Removed From Organization");
                }
            });
        }

        openOrganisationDialog(
            organisation: Organisation,
            $event: any,
            section?: string
        ): void {
            this.dialog
                .show({
                    component: "vc-create-organisation",
                    targetEvent: $event,
                    scope: this.$scope,
                    size: "large",
                    locals: {
                        organisation: organisation,
                        initialSection: section || null,
                    },
                })
                .then((editedOrganisation: Organisation) => {
                    angular.extend(organisation, editedOrganisation);
                });
        }

        xeroLabel(status: string | null | undefined): string {
            return XeroService.label(status);
        }

        xeroUrl(id: string): string {
            return XeroService.contactUrl(id);
        }

        xeroStanding(row: any): { label: string; cls: string } {
            return XeroService.standing(row);
        }

        /** Xero invoice history, the Stripe charge-history equivalent. */
        openXeroInvoices(organisation: Organisation, $event: any): void {
            if (organisation.xero_status != "matched") return;
            this.dialog.show({
                component: "vc-xero-invoices-dialog",
                targetEvent: $event,
                scope: this.$scope,
                size: "x-large",
                locals: {
                    target: { organisation: organisation.oid },
                },
            });
        }

        deleteOrganisation(organisation: Organisation, $event: any): void {
            this.dialog
                .delete({
                    targetEvent: $event,
                    locals: {
                        bodyText:
                            "Are you sure you want to permanently remove this organization?",
                        title: "Delete Organization Account",
                    },
                })
                .then(() => {
                    this.service
                        .cancel({ oid: organisation.oid })
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success) {
                                organisation.ostatus = "Deleted";
                                this.toast.success(
                                    "Organization Account Deleted"
                                );
                            } else {
                                this.toast.error();
                            }
                        });
                });
        }

        reactivateOrganisation(organisation: Organisation): void {
            this.service
                .reactivate({ oid: organisation.oid })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        organisation.ostatus = "Active";
                        this.toast.success("Organization Account Re-activated");
                    } else {
                        this.toast.error();
                    }
                });
        }

        pauseOrganisation(organisation: Organisation): void {
            this.service
                .pause({ oid: organisation.oid })
                .then((response: object) => {
                    const json = response["data"];
                    if (json.success) {
                        organisation.ostatus = "Paused";
                        this.toast.success("Organization Account Paused");
                    } else {
                        this.toast.error();
                    }
                });
        }
    },
};

angular
    .module("admin")
    .component(
        AdminOrganisationListComponent.selector,
        AdminOrganisationListComponent
    );
